import type { TExample } from '@atlas/uicorn/preview/modules';
import { useCallback, useMemo, useState } from 'react';
import * as React from 'react';
import styled, { css } from 'styled-components';
import moment, { type Moment as TMoment } from 'moment';
import type { DatePickerProps as TAntDatePickerProps } from 'antd';
import useRefCallback from 'use-ref-callback';
import useControlledState from '@atlas/tool/box/hooks/useControlledState';
import type { TTimezoneIdentifier } from '@atlas/tool/box/date/timezones';
import dayjs from '@atlas/tool/box/date/dayjs';
import momentGenerateConfig from 'rc-picker/lib/generate/moment';
import generatePicker from 'antd/lib/date-picker/generatePicker';

momentGenerateConfig.getNow = () => {
    const today = dayjs.tz(Date.now()).startOf('day');
    return moment(today.format('YYYY-MM-DD HH:mm:ss'));
};

const AntDatePicker = generatePicker<TMoment>(momentGenerateConfig);

const DatePicker = React.memo(function DatePicker(props: TDatePickerProps): React.ReactElement {
    const {
        value: propsValue,
        defaultValue: propsDefaultValue,
        onChange: propsOnChange,
        dateFormat,
        disabledDate,
        showTime,
        highlightWeek,
        timezone,
        ...elementProps
    } = props;

    const [container, setContainer] = useState<HTMLSpanElement | null>(null);
    const getPopupContainer = useRefCallback(() => container || document.body);

    const [value, setValue] = useControlledState(
        propsValue,
        (newValue) => {
            const momentValue = moment(newValue);
            if (!momentValue.isValid()) return;

            const stringValue = showTime
                ? momentValue.format((dateFormat || 'YYYY/MM/DD') + ' ' + (showTime.format || 'HH:mm'))
                : momentValue.format(dateFormat || 'YYYY/MM/DD');
            propsOnChange?.(stringValue, momentValue);
        },
        propsDefaultValue,
    );

    const momentValue = useMemo(() => (value ? moment(value) : undefined), [value]);

    const handleChange = useRefCallback((_, newValue: string) => setValue(newValue));
    const handleSelect = useRefCallback((newValue: moment.Moment) => showTime && setValue(newValue.toString()));

    const disabledPastDate = useCallback(
        (date: TMoment) => {
            const today = dayjs.tz(Date.now(), timezone).startOf('day');
            const test = dayjs.tz(date.format('YYYY-MM-DD HH:mm:ss'), timezone).startOf('day');
            return test.isBefore(today);
        },
        [timezone],
    );

    return (
        <SDatePicker
            $highlightWeek={highlightWeek}
            {...elementProps}
            children={
                container && (
                    <SAntDatePicker
                        disabledDate={disabledDate === 'past' ? disabledPastDate : disabledDate}
                        value={momentValue}
                        getPopupContainer={getPopupContainer}
                        onChange={handleChange}
                        onSelect={handleSelect}
                        showTime={showTime}
                    />
                )
            }
            ref={setContainer}
        />
    );
});

export default DatePicker;

// #region Styles

export const SAntDatePicker = styled(({ className, ...props }: TAntDatePickerProps) => (
    <AntDatePicker {...props} open size="small" popupClassName={className} />
))`
    padding: 0;

    .ant-picker-panel-container {
        background-color: ${(p) => p.theme.color.grey0};
        box-shadow: none;
    }

    .ant-picker-panel {
        border-bottom: none;
    }

    .ant-picker-header button,
    .ant-picker-cell {
        color: ${(p) => p.theme.color.grey8};
    }

    .ant-picker-header-view button,
    .ant-picker-content th,
    .ant-picker-cell-in-view,
    .ant-picker-header-view,
    .ant-picker-time-panel-column > li.ant-picker-time-panel-cell .ant-picker-time-panel-cell-inner {
        color: ${(p) => p.theme.color.grey9};
    }

    .ant-picker-time-panel-column > li.ant-picker-time-panel-cell-selected .ant-picker-time-panel-cell-inner {
        background-color: ${(p) => p.theme.color.blue5};
    }

    .ant-picker-cell:hover:not(.ant-picker-cell-in-view) .ant-picker-cell-inner,
    .ant-picker-cell:hover:not(.ant-picker-cell-selected):not(.ant-picker-cell-range-start):not(
            .ant-picker-cell-range-end
        ):not(.ant-picker-cell-range-hover-start):not(.ant-picker-cell-range-hover-end)
        .ant-picker-cell-inner,
    .ant-picker-time-panel-column > li.ant-picker-time-panel-cell .ant-picker-time-panel-cell-inner:hover {
        background-color: ${(p) => p.theme.color.grey5};
    }

    .ant-picker-cell-disabled::before {
        background-color: ${(p) => p.theme.color.grey5};
    }

    .ant-picker-footer {
        border-bottom: none;
        border-color: ${(p) => p.theme.color.grey6};
    }

    .ant-picker-footer .ant-picker-ranges {
        display: none;
    }
`;

const SDatePicker = styled.span<{ $highlightWeek?: boolean }>`
    display: inline-block;

    & > div,
    ${SAntDatePicker} {
        position: unset !important;
    }

    & > .ant-picker {
        visibility: hidden;
        display: block;
        height: 0;
        width: 0;
        padding: 0;
        margin: 0;
        border: 0;
    }

    .ant-picker-cell-in-view.ant-picker-cell-selected .ant-picker-cell-inner,
    .ant-picker-cell-in-view.ant-picker-cell-range-start .ant-picker-cell-inner,
    .ant-picker-cell-in-view.ant-picker-cell-range-end .ant-picker-cell-inner {
        background: ${(p) => p.theme.color.primary};
        color: ${(p) => p.theme.color.grey0};
    }

    ${(p) =>
        p.$highlightWeek
            ? css`
                  .ant-picker-cell:hover:not(.ant-picker-cell-in-view) .ant-picker-cell-inner,
                  .ant-picker-cell:hover:not(.ant-picker-cell-selected):not(.ant-picker-cell-range-start):not(
                          .ant-picker-cell-range-end
                      ):not(.ant-picker-cell-range-hover-start):not(.ant-picker-cell-range-hover-end)
                      .ant-picker-cell-inner,
                  .ant-picker-time-panel-column
                      > li.ant-picker-time-panel-cell
                      .ant-picker-time-panel-cell-inner:hover {
                      background-color: transparent;
                  }

                  .ant-picker-content > tbody > tr {
                      background: transparent;
                      transition: background 0.3s;
                  }

                  .ant-picker-content > tbody > tr:hover > .ant-picker-cell:not(.ant-picker-cell-selected) {
                      background-color: ${(p) => p.theme.color.grey6};

                      &:first-child {
                          border-top-left-radius: 0.2rem;
                          border-bottom-left-radius: 0.2rem;
                      }

                      &:last-child {
                          border-top-right-radius: 0.2rem;
                          border-bottom-right-radius: 0.2rem;
                      }
                  }

                  .ant-picker-cell-in-view.ant-picker-cell-selected .ant-picker-cell-inner,
                  .ant-picker-cell-in-view.ant-picker-cell-range-start .ant-picker-cell-inner,
                  .ant-picker-cell-in-view.ant-picker-cell-range-end .ant-picker-cell-inner {
                      background: transparent;
                      color: inherit;
                  }

                  .ant-picker-content > tbody > :is(tr, tr:hover) > .ant-picker-cell-selected {
                      &,
                      & ~ .ant-picker-cell {
                          background: ${(p) => p.theme.color.primary};
                          color: ${(p) => p.theme.color.grey0};
                      }

                      border-top-left-radius: 0.2rem;
                      border-bottom-left-radius: 0.2rem;

                      & ~ .ant-picker-cell:last-child {
                          border-top-right-radius: 0.2rem;
                          border-bottom-right-radius: 0.2rem;
                      }
                  }
              `
            : ''}
`;

// #endregion

// #region Types

export type TDatePickerProps = Omit<React.HTMLAttributes<HTMLDivElement>, 'children' | 'onChange'> & {
    value?: string | Date | TMoment;
    defaultValue?: string | Date | TMoment;
    onChange?: (newValue: string, momentValue: TMoment) => void;
    disabledDate?: 'past' | ((date: TMoment) => boolean);
    dateFormat?: string;
    showTime?: { format?: string | undefined };
    highlightWeek?: boolean; // Used for Scheduling to highlight the week and not the day
    timezone?: TTimezoneIdentifier;
};

// #endregion

// #region Examples

export const Example: TExample = {
    render(props) {
        return (
            <DatePicker
                value={props.value === 'None' ? undefined : props.value}
                highlightWeek={props.highlightWeek}
                onChange={props.onChange}
            />
        );
    },
    controls: {
        value: ['None', '2023/01/01', '2024/01/01'],
        highlightWeek: { type: 'boolean', initial: false },
    },
    handlers: {
        onChange: 'console',
    },
};

// #endregion

// #region Tests

if (import.meta.vitest) {
    const mockedToday = new Date(2023, 7, 24, 12); // noon 2023/08/24
    const { afterAll, beforeAll, describe, it, expect, vi } = import.meta.vitest;
    describe('DatePicker', async () => {
        const testModule = '@test';
        const { render } = await import(/* @vite-ignore */ testModule);

        beforeAll(() => {
            vi.useFakeTimers();
            vi.setSystemTime(mockedToday);
        });

        it('matches snapshot', () => {
            const result = render(<Example.render />);
            expect(result.container).toMatchSnapshot();
        });

        afterAll(() => {
            vi.useRealTimers();
        });
    });
}

// #endregion
